(function () {
  if (window.scrollFadeInit) return;
  window.scrollFadeInit = true;

  var halfObserver = null;
  var fullObserver = null;
  var containerObserver = null;
  var attachScheduled = false;

  var STAGGER_STEP_MS = 80;
  var STAGGER_MAX_STEPS = 6;
  var FADE_DURATION_MS = 800;

  // an element normally joins the stagger group of its nearest ancestor
  // .scroll-fade-grid. data-scroll-fade-group lets an element outside that
  // ancestor chain (e.g. a CTA sitting after a grid, as a sibling) join the
  // same group by id instead.
  function groupKeyFor(el) {
    var ref = el.getAttribute('data-scroll-fade-group');
    if (ref) {
      var target = document.getElementById(ref);
      if (target) return target;
    }
    return el.closest('.scroll-fade-grid');
  }

  // once an element has faded in, drop the scroll-fade machinery entirely so
  // components that reuse `opacity` for their own hover/disabled transitions
  // (e.g. buttons) get their normal transition back instead of being stuck
  // with the longer fade-in duration/delay forever
  function cleanupAfterReveal(el) {
    function cleanup() {
      el.classList.remove('scroll-fade', 'scroll-fade--visible', 'scroll-fade--half');
      el.style.removeProperty('transition-delay');
    }

    var duration = window.getComputedStyle(el).transitionDuration;
    if (!duration || parseFloat(duration) === 0) {
      cleanup();
      return;
    }

    el.addEventListener('transitionend', function handler(event) {
      if (event.target !== el || event.propertyName !== 'opacity') return;
      el.removeEventListener('transitionend', handler);
      cleanup();
    });
  }

  // reveals elements in DOM order, one after another, when several of them
  // (e.g. cards in a grid) enter the viewport within the same batch
  function revealStaggered(elements) {
    var byGroup = new Map();
    var ungrouped = [];

    elements
      .sort(function (a, b) {
        return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
      })
      .forEach(function (el) {
        var group = groupKeyFor(el);
        if (!group) {
          ungrouped.push(el);
          return;
        }
        if (!byGroup.has(group)) byGroup.set(group, []);
        byGroup.get(group).push(el);
      });

    ungrouped.forEach(function (el) {
      el.classList.add('scroll-fade--visible');
      cleanupAfterReveal(el);
    });

    byGroup.forEach(function (group, groupKey) {
      // a group can opt into a slower/faster stagger via data-scroll-fade-step
      // (ms per step) on its .scroll-fade-grid element, instead of the default
      var stepMs = parseInt(groupKey.getAttribute('data-scroll-fade-step'), 10);
      if (!(stepMs >= 0)) stepMs = STAGGER_STEP_MS;

      // trailing elements (e.g. a CTA after a grid) don't take their own step:
      // they wait for the last regular item's step to finish fading in first
      var items = group.filter(function (el) {
        return !el.classList.contains('scroll-fade-trailing');
      });
      var trailing = group.filter(function (el) {
        return el.classList.contains('scroll-fade-trailing');
      });
      var lastStep = 0;

      items.forEach(function (el, index) {
        var step = Math.min(index, STAGGER_MAX_STEPS);
        lastStep = Math.max(lastStep, step);
        el.style.transitionDelay = (step * stepMs) + 'ms';
        el.classList.add('scroll-fade--visible');
        cleanupAfterReveal(el);
      });

      trailing.forEach(function (el) {
        el.style.transitionDelay = (lastStep * stepMs + FADE_DURATION_MS) + 'ms';
        el.classList.add('scroll-fade--visible');
        cleanupAfterReveal(el);
      });
    });
  }

  function makeObserver(threshold) {
    return new IntersectionObserver(function (entries, observer) {
      var toReveal = [];
      entries.forEach(function (entry) {
        // an element taller than the viewport can never reach full visibility
        var tall = entry.boundingClientRect.height >= window.innerHeight;
        if (entry.intersectionRatio >= threshold || (tall && entry.intersectionRatio >= 0.5)) {
          toReveal.push(entry.target);
          observer.unobserve(entry.target);
        }
      });
      if (toReveal.length) revealStaggered(toReveal);
    }, { threshold: [0.5, threshold] });
  }

  // for elements whose cards live inside their own scrollable area (e.g. a
  // sidebar list with overflow: auto), a card scrolled past the internal
  // fold never reaches a real intersection ratio on its own and would stay
  // invisible forever. Instead we watch the container itself and, as soon
  // as any part of it is on screen, reveal every card inside it in one go.
  function makeContainerObserver() {
    return new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        var pending = Array.prototype.filter.call(
          entry.target.querySelectorAll('.scroll-fade'),
          function (el) {
            return !el.classList.contains('scroll-fade--visible');
          }
        );
        if (pending.length) revealStaggered(pending);
      });
    }, { threshold: 0.1 });
  }

  // for content whose inner markup isn't authored per-element (e.g. rich
  // text from a metafield), .scroll-fade-children turns each direct child
  // into its own staggered scroll-fade item automatically
  function expandChildGroups() {
    var groups = document.querySelectorAll('.scroll-fade-children:not([data-scroll-fade-expanded])');
    groups.forEach(function (el) {
      el.setAttribute('data-scroll-fade-expanded', '');
      el.classList.add('scroll-fade-grid');
      Array.prototype.forEach.call(el.children, function (child) {
        child.classList.add('scroll-fade');
      });
    });
  }

  function attach() {
    expandChildGroups();

    var containers = document.querySelectorAll('.scroll-fade-container:not([data-scroll-fade])');
    var els = document.querySelectorAll('.scroll-fade:not([data-scroll-fade])');
    if (!containers.length && !els.length) return;

    if (!('IntersectionObserver' in window)) {
      containers.forEach(function (el) {
        el.setAttribute('data-scroll-fade', '');
      });
      els.forEach(function (el) {
        el.setAttribute('data-scroll-fade', '');
        el.classList.add('scroll-fade--visible');
      });
      return;
    }

    if (!halfObserver) {
      halfObserver = makeObserver(0.5);
      fullObserver = makeObserver(1);
    }
    if (!containerObserver) containerObserver = makeContainerObserver();

    containers.forEach(function (el) {
      el.setAttribute('data-scroll-fade', '');
      containerObserver.observe(el);
    });

    els.forEach(function (el) {
      // cards inside a scroll-fade-container are revealed as a group by
      // the container observer above, not individually
      if (el.closest('.scroll-fade-container')) {
        el.setAttribute('data-scroll-fade', '');
        return;
      }

      el.setAttribute('data-scroll-fade', '');
      if (el.classList.contains('scroll-fade--half')) {
        halfObserver.observe(el);
      } else {
        fullObserver.observe(el);
      }
    });
  }

  function scheduleAttach() {
    if (attachScheduled) return;
    attachScheduled = true;
    requestAnimationFrame(function () {
      attachScheduled = false;
      attach();
    });
  }

  function init() {
    attach();

    // theme editor re-renders a section
    document.addEventListener('shopify:section:load', scheduleAttach);

    // hot reload (shopify theme dev) swaps DOM nodes without firing an event
    if ('MutationObserver' in window) {
      new MutationObserver(scheduleAttach).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
